import type { AssetVehicleRow } from './assets-types';
import { setAssetsSortCol, setAssetsSortAsc, assetsSortCol, assetsSortAsc, fmtPct, fmtHrs, fmtKm, fmtDate } from './assets-page';
import { auRecalculate } from './assets-page';
import { escapeHtml, escapeHtmlAttr } from '../utils/html';

// ── State toggle ──────────────────────────────────────────────────────────

export function showAssetsState(
  s: 'empty' | 'loading' | 'error' | 'results',
  msg = ''
): void {
  document.getElementById('auEmpty')!   .classList.toggle('hidden', s !== 'empty');
  document.getElementById('auLoading')! .classList.toggle('hidden', s !== 'loading');
  document.getElementById('auErrBox')!  .classList.toggle('hidden', s !== 'error');
  document.getElementById('auResults')! .classList.toggle('hidden', s !== 'results');

  if (s === 'loading') {
    const lbl = document.getElementById('auLoadingLabel');
    if (lbl) lbl.textContent = msg || 'Loading…';
  }
  if (s === 'error') {
    const box = document.getElementById('auErrBox')!;
    box.textContent = msg;
    // Also show empty state beneath
    document.getElementById('auEmpty')!.classList.remove('hidden');
  }
}

// ── Main render entry ─────────────────────────────────────────────────────

export function renderAssetsResults(
  rows: AssetVehicleRow[],
  sortCol: string,
  sortAsc: boolean
): void {
  renderAssetsKPIs(rows);
  renderAssetsChart(rows);
  renderAssetsTable(rows, sortCol, sortAsc);

  const countEl = document.getElementById('auCount');
  if (countEl) countEl.textContent = rows.length + ' vehicles';
}

// ── KPI cards ─────────────────────────────────────────────────────────────

function renderAssetsKPIs(rows: AssetVehicleRow[]): void {
  const grid = document.getElementById('auKpiGrid')!;
  if (!rows.length) { grid.innerHTML = ''; return; }

  const active   = rows.filter((r) => r.tripCount > 0);
  const dormant  = rows.filter((r) => r.tripCount === 0);
  const avgDay   = rows.reduce((s, r) => s + r.dayUtilPct, 0) / rows.length;
  const avgHr    = rows.reduce((s, r) => s + r.hrUtilPct,  0) / rows.length;
  const mostUtil = [...rows].sort((a, b) => b.dayUtilPct - a.dayUtilPct)[0];
  const leastNonDormant = [...active].sort((a, b) => a.dayUtilPct - b.dayUtilPct)[0];
  const totalKm  = rows.reduce((s, r) => s + r.totalDistanceKm, 0);

  grid.innerHTML = `
    <div class="kpi-card kpi-hi">
      <div class="kpi-val">${fmtPct(avgDay)}</div>
      <div class="kpi-lbl">Fleet Avg Day Util</div>
      <div class="kpi-sub">days active ÷ days in period</div>
    </div>
    <div class="kpi-card kpi-hi">
      <div class="kpi-val">${fmtPct(avgHr)}</div>
      <div class="kpi-lbl">Fleet Avg Hours Util</div>
      <div class="kpi-sub">engine hrs ÷ available hrs</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${mostUtil ? fmtPct(mostUtil.dayUtilPct) : '—'}</div>
      <div class="kpi-lbl">Most Utilized</div>
      <div class="kpi-sub">${mostUtil ? escapeHtml(mostUtil.name) : '—'}</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${leastNonDormant ? fmtPct(leastNonDormant.dayUtilPct) : '—'}</div>
      <div class="kpi-lbl">Least Active</div>
      <div class="kpi-sub">${leastNonDormant ? escapeHtml(leastNonDormant.name) : 'None active'}</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-val">${active.length} <span style="font-size:0.9rem;font-weight:400;color:var(--muted)">/ ${rows.length}</span></div>
      <div class="kpi-lbl">Active Vehicles</div>
      <div class="kpi-sub">${fmtKm(totalKm)} total distance</div>
    </div>
    <div class="kpi-card${dormant.length > 0 ? ' kpi-danger' : ''}">
      <div class="kpi-val">${dormant.length}</div>
      <div class="kpi-lbl">Dormant Vehicles</div>
      <div class="kpi-sub">${dormant.length > 0 ? 'zero trips in period' : 'all vehicles active'}</div>
    </div>
  `.trim();
}

// ── Horizontal bar chart ──────────────────────────────────────────────────

function renderAssetsChart(rows: AssetVehicleRow[]): void {
  const wrap = document.getElementById('auChart')!;
  if (!rows.length) { wrap.innerHTML = ''; return; }

  const top20 = [...rows]
    .sort((a, b) => b.dayUtilPct - a.dayUtilPct)
    .slice(0, 20);

  const barsHtml = top20.map((r) => {
    const color = r.dayUtilPct >= 75 ? 'var(--au-high)'
                : r.dayUtilPct >= 40 ? 'var(--au-mid)'
                : r.dayUtilPct  > 0  ? 'var(--au-low)'
                : 'var(--au-zero)';

    return `
      <div class="au-chart-row">
        <div class="au-chart-label" title="${escapeHtmlAttr(r.name)}">${escapeHtml(r.name)}</div>
        <div class="au-chart-bar-track">
          <div class="au-chart-bar-fill" style="width:${r.dayUtilPct.toFixed(1)}%;background:${color}"></div>
          <div class="au-chart-bar-outline" style="width:${r.hrUtilPct.toFixed(1)}%"></div>
        </div>
        <div class="au-chart-pct" style="color:${color}">${fmtPct(r.dayUtilPct)}</div>
      </div>
    `.trim();
  }).join('');

  wrap.innerHTML = `
    <div class="au-chart-card">
      <div class="au-chart-title">
        <span class="au-chart-title-text">Top Vehicles by Utilization</span>
        <span class="au-chart-legend">
          <span class="au-legend-day">● DAY %</span>
          <span class="au-legend-hr">░ HRS %</span>
        </span>
      </div>
      <div class="au-chart-body">${barsHtml}</div>
    </div>
  `.trim();
}

// ── Table ─────────────────────────────────────────────────────────────────

export function renderAssetsTable(
  rows: AssetVehicleRow[],
  sortCol: string,
  sortAsc: boolean
): void {
  type ColKey = keyof AssetVehicleRow;

  const cols: { key: ColKey; label: string; fmt: (r: AssetVehicleRow) => string }[] = [
    { key: 'name',            label: 'Vehicle',     fmt: (r) => `<strong>${escapeHtml(r.name)}</strong>` },
    { key: 'group',           label: 'Group',        fmt: (r) => `<span class="td-muted">${escapeHtml(r.group)}</span>` },
    { key: 'dayUtilPct',      label: 'Day Util %',   fmt: (r) => auBarCell(r.dayUtilPct, 'day') },
    { key: 'activeDays',      label: 'Active Days',  fmt: (r) => `<span class="td-mono">${r.activeDays}<span class="td-muted"> / ${r.totalDays}</span></span>` },
    { key: 'hrUtilPct',       label: 'Hrs Util %',   fmt: (r) => auBarCell(r.hrUtilPct, 'hr') },
    { key: 'totalEngineHrs',  label: 'Engine Hrs',   fmt: (r) => `<span class="td-mono">${fmtHrs(r.totalEngineHrs)}<span class="td-muted"> / ${fmtHrs(r.availableHrs)}</span></span>` },
    { key: 'tripCount',       label: 'Trips',        fmt: (r) => `<span class="td-mono">${r.tripCount}</span>` },
    { key: 'totalDistanceKm', label: 'Distance',     fmt: (r) => `<span class="td-mono">${fmtKm(r.totalDistanceKm)}</span>` },
    { key: 'avgTripKm',       label: 'Avg Trip',     fmt: (r) => `<span class="td-mono">${r.tripCount > 0 ? fmtKm(r.avgTripKm) : '—'}</span>` },
    { key: 'lastTripDate',    label: 'Last Trip',    fmt: (r) => `<span class="td-muted">${fmtDate(r.lastTripDate)}</span>` },
  ];

  const sorted = [...rows].sort((a, b) => {
    const av = a[sortCol as ColKey];
    const bv = b[sortCol as ColKey];
    const dir = sortAsc ? 1 : -1;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return typeof av === 'string'
      ? (av as string).localeCompare(bv as string) * dir
      : ((av as number) - (bv as number)) * dir;
  });

  const thHTML = cols.map((c) => {
    const active = c.key === sortCol;
    const arrow  = active ? (sortAsc ? '▲' : '▼') : '▲';
    return `<th class="${active ? 'sort-active' : ''}" onclick="window.__auSortBy('${c.key}')">${c.label}<span class="sort-arrow ${active ? '' : 'sort-arrow-dim'}">${arrow}</span></th>`;
  }).join('');

  const rowsHTML = sorted.map((r) => {
    const cls = r.dayUtilPct >= 75 ? 'au-high'
              : r.dayUtilPct >= 40 ? 'au-mid'
              : r.dayUtilPct  > 0  ? 'au-low'
              : 'au-zero';
    return `<tr class="${cls}">${cols.map((c) => `<td>${c.fmt(r)}</td>`).join('')}</tr>`;
  }).join('');

  (document.getElementById('auTable') as HTMLElement).innerHTML =
    `<thead><tr>${thHTML}</tr></thead><tbody>${rowsHTML}</tbody>`;

  (window as Window & { __auSortBy?: (col: string) => void }).__auSortBy = (col: string) => {
    if (assetsSortCol === col) setAssetsSortAsc(!assetsSortAsc);
    else { setAssetsSortCol(col); setAssetsSortAsc(false); }
    auRecalculate();
  };
}

// ── Inline bar cell ───────────────────────────────────────────────────────

function auBarCell(pct: number, type: 'day' | 'hr'): string {
  const color = pct >= 75 ? 'var(--au-high)'
              : pct >= 40 ? 'var(--au-mid)'
              : pct  > 0  ? 'var(--au-low)'
              : 'var(--muted)';
  const label = fmtPct(pct);
  const barType = type === 'day' ? 'au-bar-day' : 'au-bar-hr';
  return `
    <div class="au-bar-cell">
      <div class="au-bar-track">
        <div class="au-bar-fill ${barType}" style="width:${Math.min(pct, 100).toFixed(1)}%;background:${color}"></div>
      </div>
      <span class="td-mono" style="color:${color}">${label}</span>
    </div>
  `.trim();
}
